<template>
    <div class="py-12 bg-white">
        <div class="bg-white">
            <div class="px-12 container">
                <div class="relative">
                    <h1 class="text-blue text-3xl text-center">Select Your Cloud Server Options</h1>
                    <router-link
                        to="/order/vps"
                        class="-ml-10 sm:ml-0 absolute left-0 top-0 text-3xl text-grey-700"
                    >
                        <i class="far fa-arrow-left"></i>
                    </router-link>
                    <hr class="border-green w-8 mb-12 mx-auto">
                </div>
            </div>

        </div>

        <div class="bg-grey-100">
            <div class="p-12 container">
                <div class="flex flex-col mb-4">
                    <p class="mb-0">{{ group }}<strong style="color:#bed734">Plus</strong></p>
                    <h3>{{ selectedProductName }}</h3>
                </div>
                <div class="flex mb-4">
                    <icon
                        icon="delivery"
                        class="pt-1 mr-4 w-12 h-12 text-green fill-current"
                    />
                    <div>
                        <h3 class="text-base mb-0">Current delivery time on this server:</h3>
                        <p class="text-green text-xl mb-0">60 seconds!</p>
                    </div>
                </div>
                <div class="flex flex-col md:flex-row">
                    <div class="mb-6 mr-6 flex flex-row md:flex-col md:text-center md:mb-0">
                        <icon
                            icon="vps-light"
                            class="block w-20 h-20 fill-current stroke-current mr-4 md:mr-0"
                        />
                        <div>
                            <p class="text-green text-xl mb-0 sau-font-bold">
                                {{ total() }}
                            </p>
                            <p class="text-sm m-0">
                                Per month<br />
                                (Ex. GST)
                            </p>
                        </div>
                    </div>
                    <div class="w-56 mt-2 md:mx-6">
                        <p class="mb-2 lg:mb-0">
                            <strong class="block w-full lg:inline-block lg:w-auto sau-font-bold mr-2">CPU Cores:</strong> {{ specifications().cpu }}
                        </p>
                        <p class="mb-2 lg:mb-0">
                            <strong class="block w-full lg:inline-block lg:w-auto sau-font-bold mr-2">Memory:</strong> {{ specifications().memory }}
                        </p>
                        <p class="mb-2 lg:mb-0">
                            <strong class="block w-full lg:inline-block lg:w-auto sau-font-bold mr-2">SSD Storage:</strong> {{ specifications().storage }}
                        </p>
                        <p class="mb-2 lg:mb-0">
                            <strong class="block w-full lg:inline-block lg:w-auto sau-font-bold mr-2">Data:</strong> {{ specifications().data }}
                        </p>
                    </div>
                    <div class="flex-1 md:mt-2 md:mx-6">
                        <p class="mb-2 lg:mb-0">
                            <strong class="block w-full lg:inline-block lg:w-auto sau-font-bold mr-2">Operating System:</strong> {{ specifications().os }}
                        </p>
                        <p class="mb-2 lg:mb-0">
                            <strong class="block w-full lg:inline-block lg:w-auto sau-font-bold mr-2">IP Addresses:</strong> {{ specifications().ip }}
                        </p>
                        <p class="mb-2 lg:mb-0">
                            <strong class="block w-full lg:inline-block lg:w-auto sau-font-bold mr-2">Additional Licences:</strong> {{ specifications().licences.replace('License - ', '') }}
                        </p>
                        <!-- <p class="mb-0">
                            <strong class="block w-full lg:inline-block lg:w-auto sau-font-bold mr-2">Management:</strong> {{ specifications().management }}
                        </p> -->
                    </div>
                </div>
            </div>
        </div>

        <div class="pt-12 bg-white">
            <div class="px-12 container">
                <div class="mb-4">
                    <label for="input-os">Operating System Image</label>
                    <div class="sau-bx">
                        <select id="input-os" class="form-control sau-select" v-model="form.os_id" :disabled="submitting">
                            <option v-for="option in options.os" :value="option.id" :key="option.id">
                                {{ optionDisplayName(option) }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="mb-4">
                    <label for="input-ip">IP Addresses</label>
                    <div class="sau-bx">
                        <select id="input-ip" class="form-control sau-select" v-model="form.ip_id" :disabled="submitting">
                            <option v-for="option in options.ip" :value="option.id" :key="option.id">
                                {{ optionDisplayName(option) }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="mb-4">
                    <label for="input-hostname">Hostname</label>
                    <input
                        type="text"
                        id="input-hostname"
                        :disabled="submitting"
                        v-model="form.hostname"
                        class="form-control sau-bx w-100"
                        placeholder="myserver.example.com"
                    />
                    <p v-if="! hostnameValid" class="mb-0"><small class="text-small text-red">Sorry, that hostname is not valid</small></p>
                    <small class="text-xs text-grey-700">
                        If no hostname is provided, one will automatically be assigned for you.
                    </small>
                </div>

                <div class="mb-4" v-if="selectedOsSupportsLicences">
                    <label for="input-licence">Software Licence</label>
                    <div class="sau-bx">
                        <select id="input-licence" class="form-control sau-select" v-model="form.licence_id" :disabled="submitting">
                            <option :value="null">None</option>
                            <option v-for="option in options.addons.licences" :value="option.id" :key="option.id">
                                <template v-if="option.price[userCurrency] > 0">{{ option.name.replace('License - ', '') }} - {{ option.price[userCurrency] | currency }}</template>
                                <template v-else>{{ option.name.replace('License - ', '') }}</template>
                            </option>
                        </select>
                    </div>
                    <small v-if="licence && licence.description" class="text-xs text-grey-700" v-html="licence.description"></small>
                </div>

                <!-- <div class="mb-20">
                    <label for="input-management">Management</label>
                    <div class="sau-bx">
                        <select id="input-management" class="form-control sau-select" v-model="form.management_id" :disabled="submitting">
                            <option v-for="option in options.addons.management" :value="option.id" :key="option.id">
                                {{ optionDisplayName(option) }}
                            </option>
                        </select>
                    </div>
                </div> -->

                <button class="btn btn-success px-16 mx-auto block" @click="submit()" :disabled="submitting || ! hostnameValid">
                    <template v-if="submitting">
                        Please wait... <i class="far fa-spinner-third fa-spin"></i>
                    </template>
                    <template v-else>
                        {{ updating ? 'Update Order' : 'Add to Order' }}
                    </template>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        form: Object,
        options: Object,
        licence: Object,
        updating: Boolean,
        submitting: Boolean,
        selectedProduct: Object
    },

    inject: [
        'total',
        'submit',
        'specifications',
        'optionDisplayName',
    ],

    data() {
        return {
            hostnameRegex: /^([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])(\.([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]))*$/,
        }
    },

    computed: {
        selectedOs() {
            return this.options.os.find(os => os.id === this.form.os_id);
        },
        selectedOsSupportsLicences() {
            if(! _.get(this, 'selectedOs.name', false)) return false;
            return this.selectedOs.name.toLowerCase().includes('cpanel auto install');
        },
        hostnameValid() {
            if(this.form && typeof this.form.hostname === 'string' && this.form.hostname.length > 0) {
                return this.hostnameRegex.test(this.form.hostname) && this.form.hostname.length < 255;
            }
            return true;
        },
        selectedProductName() {
            return this.selectedProduct != null && this.selectedProduct.hasOwnProperty('name') ? this.selectedProduct.name : '';
        },
        group() {
            switch (this.selectedProductName.slice(0, 1)) {
                case 's':
                    return 'Storage';
                case 'e':
                default:
                    return 'Essentials';
            }


        }
    },

    watch: {
        selectedOsSupportsLicences: function() {
            if( ! this.selectedOsSupportsLicences) {
                this.form.licence_id = '';
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.sau-select {
    border: none;
    text-align-last: center; // Chrome
    text-align: center; // Firefox
    padding-top: 0px !important;
    &:focus,
    &:hover {
        outline: none;
        box-shadow: none;
        border: none;
    }
}
select {
    -webkit-appearance: auto !important;
    -moz-appearance: auto !important;
}
</style>
